import React, { Component } from "react";
import { connect } from "react-redux";
import { Button, Header, Popup } from "../../components";
import { Text } from "../../components/text";
import { siteConfigActions } from "../../webapi";
import { isTheBest } from "../../session";
import { COLOUR_GREEN, COLOUR_RED, TEXT_LIGHT } from "../../js";

const TRANSFER_VERSION = 1;

const CONFIG_SECTIONS = [
	"featurePicker",
	"userTypes",
	"maintenanceJobTypes",
	"maintenanceWorkflow",
	"formsJobTypes",
	"formsWorkflow",
];

const SECTION_LABELS = {
	featurePicker: "Feature Picker",
	userTypes: "User Types",
	maintenanceJobTypes: "Maintenance Job Types",
	maintenanceWorkflow: "Maintenance Workflow",
	formsJobTypes: "Forms Job Types",
	formsWorkflow: "Forms Workflow",
};

/**
 * Recursively strip _mode fields from an object
 */
const stripModeFields = (obj) => {
	if (Array.isArray(obj)) {
		return obj.map(stripModeFields);
	}
	if (obj && typeof obj === "object") {
		const result = {};
		for (const [key, value] of Object.entries(obj)) {
			if (key === "_mode") continue;
			result[key] = stripModeFields(value);
		}
		return result;
	}
	return obj;
};

/**
 * Strip site-specific DB keys from config items
 */
const stripSiteKeys = (obj) => {
	if (Array.isArray(obj)) {
		return obj.map(stripSiteKeys);
	}
	if (obj && typeof obj === "object") {
		const { site, Site, ...rest } = obj;
		const result = {};
		for (const [key, value] of Object.entries(rest)) {
			result[key] = stripSiteKeys(value);
		}
		return result;
	}
	return obj;
};

/**
 * Get a summary description for a config section
 */
const getSectionSummary = (key, data) => {
	if (!data) return null;
	switch (key) {
		case "featurePicker": {
			const hiddenCount = (data.Hidden || []).length;
			const notHiddenCount = (data.NotHidden || []).length;
			const settingsCount = data.Settings
				? Object.keys(data.Settings).length
				: 0;
			return `${hiddenCount} hidden, ${notHiddenCount} not hidden, ${settingsCount} settings keys`;
		}
		case "userTypes":
			return `${(data.items || []).length} types (${(data.items || []).map((t) => t.typeName).join(", ")})`;
		case "maintenanceJobTypes":
		case "formsJobTypes":
			return `${(data.items || []).length} types (${(data.items || []).map((t) => t.typeName).join(", ")})`;
		case "maintenanceWorkflow":
		case "formsWorkflow":
			return `${(data.statuses || []).length} statuses (${(data.statuses || []).map((s) => s.text).join(", ")})`;
		default:
			return null;
	}
};

class SiteConfigTransfer extends Component {
	constructor(props) {
		super(props);
		this.state = {
			mode: "export",
			loading: false,
			exportData: null,
			importText: "",
			importParsed: null,
			importError: null,
			importResult: null,
			showConfirmation: false,
			copied: false,
		};
	}

	// ==================== EXPORT ====================

	onExport = async () => {
		this.setState({ loading: true, exportData: null });
		try {
			const { data } = await siteConfigActions.getTemplateFromSite(
				this.props.auth.site,
			);
			const template = data.template;

			// Filter to only the 6 config sections
			const config = {};
			for (const key of CONFIG_SECTIONS) {
				if (template[key]) {
					config[key] = template[key];
				}
			}

			// Clean: strip _mode fields and site-specific keys
			const cleaned = stripSiteKeys(stripModeFields(config));

			// Wrap in transfer envelope
			const envelope = {
				_version: TRANSFER_VERSION,
				_exportedAt: new Date().toISOString(),
				_source: {
					environment: window.location.hostname,
					siteId: this.props.auth.site,
				},
				config: cleaned,
			};

			this.setState({
				exportData: JSON.stringify(envelope, null, 2),
				loading: false,
			});
		} catch (error) {
			console.log("Export failed:", error);
			this.setState({ loading: false });
			alert("Failed to export site configuration. Please try again.");
		}
	};

	onCopy = () => {
		if (!this.state.exportData) return;
		navigator.clipboard.writeText(this.state.exportData).then(() => {
			this.setState({ copied: true });
			setTimeout(() => this.setState({ copied: false }), 2000);
		});
	};

	// ==================== IMPORT ====================

	onValidate = () => {
		const { importText } = this.state;
		this.setState({
			importParsed: null,
			importError: null,
			importResult: null,
		});

		// Parse JSON
		let parsed;
		try {
			parsed = JSON.parse(importText);
		} catch (e) {
			this.setState({
				importError: "Invalid JSON. Please check the pasted content.",
			});
			return;
		}

		// Check version
		if (parsed._version !== TRANSFER_VERSION) {
			this.setState({
				importError: `Unsupported version. Expected _version: ${TRANSFER_VERSION}`,
			});
			return;
		}

		// Check config exists
		if (!parsed.config || typeof parsed.config !== "object") {
			this.setState({
				importError: 'Missing or invalid "config" object in transfer data.',
			});
			return;
		}

		// Check for unknown sections
		const configKeys = Object.keys(parsed.config);
		const unknownKeys = configKeys.filter((k) => !CONFIG_SECTIONS.includes(k));
		if (unknownKeys.length > 0) {
			this.setState({
				importError: `Unknown config sections: ${unknownKeys.join(", ")}`,
			});
			return;
		}

		if (configKeys.length === 0) {
			this.setState({ importError: "Config contains no sections to import." });
			return;
		}

		this.setState({ importParsed: parsed });
	};

	onApply = () => {
		this.setState({ showConfirmation: true });
	};

	onConfirmApply = async () => {
		this.setState({
			showConfirmation: false,
			loading: true,
			importResult: null,
		});
		try {
			const { data } = await siteConfigActions.applySiteConfigTransfer(
				this.props.auth.site,
				this.state.importParsed,
			);
			this.setState({ importResult: data, loading: false });
		} catch (error) {
			const errorMsg =
				(error.response && error.response.data && error.response.data.error) ||
				error.message ||
				"Unknown error";
			this.setState({
				importResult: {
					success: false,
					errors: [{ section: "request", error: errorMsg }],
				},
				loading: false,
			});
		}
	};

	// ==================== RENDER ====================

	renderModeToggle() {
		const { mode } = this.state;
		return (
			<div className="flex flex-row marginBottom-24">
				<Button
					buttonType={mode === "export" ? "primaryAction" : "outlined"}
					onClick={() =>
						this.setState({
							mode: "export",
							importParsed: null,
							importError: null,
							importResult: null,
						})
					}
					style={{ marginRight: 8 }}
				>
					Export
				</Button>
				<Button
					buttonType={mode === "import" ? "primaryAction" : "outlined"}
					onClick={() =>
						this.setState({ mode: "import", exportData: null, copied: false })
					}
				>
					Import
				</Button>
			</div>
		);
	}

	renderExport() {
		const { loading, exportData, copied } = this.state;
		return (
			<div>
				<Text type="body" className="marginBottom-16">
					Export the current site's configuration as JSON. You can then paste it
					into another site's import to transfer settings.
				</Text>
				<Button
					buttonType="primaryAction"
					onClick={this.onExport}
					isActive={!loading}
				>
					{loading ? "Exporting..." : "Export Current Site Config"}
				</Button>
				{exportData && (
					<div className="marginTop-24">
						<div className="flex flex-row flex-between marginBottom-8">
							<Text type="formTitleSmall">Exported JSON</Text>
							<Button
								buttonType="outlined"
								onClick={this.onCopy}
								style={{ minWidth: 140 }}
							>
								{copied ? "Copied!" : "Copy to Clipboard"}
							</Button>
						</div>
						<textarea
							readOnly
							value={exportData}
							style={{
								width: "100%",
								height: 400,
								fontFamily: "monospace",
								fontSize: 12,
								padding: 12,
								border: "1px solid #ddd",
								borderRadius: 4,
								resize: "vertical",
								backgroundColor: "#f8f9fa",
							}}
						/>
					</div>
				)}
			</div>
		);
	}

	renderImportSummary() {
		const { importParsed } = this.state;
		if (!importParsed) return null;

		const sections = Object.keys(importParsed.config);
		return (
			<div
				className="marginTop-16"
				style={{
					border: "1px solid #ddd",
					borderRadius: 4,
					padding: 16,
					backgroundColor: "#f8f9fa",
				}}
			>
				<Text type="formTitleSmall" className="marginBottom-8">
					Transfer Summary
				</Text>
				{importParsed._source && (
					<Text
						type="body"
						className="marginBottom-8"
						style={{ color: TEXT_LIGHT }}
					>
						Source: {importParsed._source.environment}
						{importParsed._source.siteId
							? ` (${importParsed._source.siteId})`
							: ""}
						{importParsed._exportedAt
							? ` - Exported: ${new Date(importParsed._exportedAt).toLocaleString()}`
							: ""}
					</Text>
				)}
				{sections.map((key) => (
					<div key={key} className="marginBottom-4">
						<Text type="body">
							<strong>{SECTION_LABELS[key] || key}</strong>:{" "}
							{getSectionSummary(key, importParsed.config[key]) || "present"}
						</Text>
					</div>
				))}
				<div
					className="marginTop-16"
					style={{ padding: 12, backgroundColor: "#fff3cd", borderRadius: 4 }}
				>
					<Text type="body">
						<strong>Warning:</strong> This will overwrite the existing
						configuration for the sections listed above on the current site.
					</Text>
				</div>
				<Button
					buttonType="primaryAction"
					onClick={this.onApply}
					style={{ marginTop: 16 }}
				>
					Apply to Current Site
				</Button>
			</div>
		);
	}

	renderImportResult() {
		const { importResult } = this.state;
		if (!importResult) return null;

		return (
			<div
				className="marginTop-16"
				style={{ border: "1px solid #ddd", borderRadius: 4, padding: 16 }}
			>
				<Text type="formTitleSmall" className="marginBottom-8">
					Import Results
				</Text>
				{importResult.applied && importResult.applied.length > 0 && (
					<div className="marginBottom-8">
						<Text type="body" style={{ color: COLOUR_GREEN }}>
							Applied: {importResult.applied.join(", ")}
						</Text>
					</div>
				)}
				{importResult.skipped && importResult.skipped.length > 0 && (
					<div className="marginBottom-8">
						<Text type="body" style={{ color: TEXT_LIGHT }}>
							Skipped: {importResult.skipped.join(", ")}
						</Text>
					</div>
				)}
				{importResult.errors && importResult.errors.length > 0 && (
					<div className="marginBottom-8">
						{importResult.errors.map((err, i) => (
							<Text key={i} type="body" style={{ color: COLOUR_RED }}>
								Error ({err.section}): {err.error}
							</Text>
						))}
					</div>
				)}
				<Text type="body" className="marginTop-8">
					{importResult.success
						? "All sections applied successfully."
						: "Some sections had errors. See details above."}
				</Text>
			</div>
		);
	}

	renderImport() {
		const { loading, importText, importError } = this.state;
		return (
			<div>
				<Text type="body" className="marginBottom-16">
					Paste exported config JSON below and click Validate to preview, then
					Apply to import.
				</Text>
				<textarea
					value={importText}
					onChange={(e) =>
						this.setState({
							importText: e.target.value,
							importParsed: null,
							importError: null,
							importResult: null,
						})
					}
					placeholder="Paste exported JSON here..."
					style={{
						width: "100%",
						height: 300,
						fontFamily: "monospace",
						fontSize: 12,
						padding: 12,
						border: "1px solid #ddd",
						borderRadius: 4,
						resize: "vertical",
					}}
				/>
				{importError && (
					<Text
						type="body"
						className="marginTop-8"
						style={{ color: COLOUR_RED }}
					>
						{importError}
					</Text>
				)}
				<Button
					buttonType="primaryAction"
					onClick={this.onValidate}
					isActive={!loading && importText.trim().length > 0}
					style={{ marginTop: 12 }}
				>
					Validate
				</Button>
				{this.renderImportSummary()}
				{this.renderImportResult()}
			</div>
		);
	}

	renderConfirmation() {
		if (!this.state.showConfirmation) return null;
		return (
			<Popup
				title="Confirm Config Transfer"
				onClose={() => this.setState({ showConfirmation: false })}
				hasPadding
				buttons={[
					{
						text: "Cancel",
						type: "outlined",
						onClick: () => this.setState({ showConfirmation: false }),
					},
					{
						text: "Apply",
						type: "primaryAction",
						onClick: this.onConfirmApply,
					},
				]}
			>
				<Text type="body">
					This will overwrite the configuration for the included sections on
					site <strong>{this.props.auth.site}</strong>. This action cannot be
					undone. Are you sure you want to proceed?
				</Text>
			</Popup>
		);
	}

	render() {
		if (!isTheBest(this.props.auth, true)) {
			return (
				<div>
					<Header />
					<div className="pageContainer container600 paddingVertical-20 paddingHorizontal-40">
						<Text type="h1">Access Denied</Text>
						<Text type="body">
							This tool is only available to plussSpace Masters.
						</Text>
					</div>
				</div>
			);
		}

		return (
			<div>
				<Header />
				<div className="pageContainer container600 paddingVertical-20 paddingHorizontal-40">
					<Text type="h1" className="marginBottom-24">
						Site Config Transfer
					</Text>
					<Text
						type="body"
						className="marginBottom-16"
						style={{ color: TEXT_LIGHT }}
					>
						Transfer Feature Picker, User Types, Forms, and Maintenance config
						between sites or environments.
					</Text>
					{this.renderModeToggle()}
					{this.state.mode === "export"
						? this.renderExport()
						: this.renderImport()}
					{this.renderConfirmation()}
				</div>
			</div>
		);
	}
}

const mapStateToProps = (state) => {
	const { auth } = state;
	return { auth };
};

export default connect(mapStateToProps, {})(SiteConfigTransfer);
